import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import { devError } from '../../../../utils/devLog';

import { Context } from '../Wizard/WizardWrapper';
import { axiosGet } from '../../../../utils/axiosGet';
import { isValidEmail } from '../../../../utils/isValidEmail';

const InputEmail = ({ name = {} }) => {
  const { register, setValue, setError, watch, errors } = useContext(Context);
  const watchEmail = watch(name);

  useEffect(() => {
    if (!/\breferee.*email\b/i.test(name)) return undefined;
    if (!watchEmail || !isValidEmail(watchEmail)) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const fellowsResponse = await axiosGet(
          `fellows?filters[email][$eq]=${watchEmail}`,
        );
        if (cancelled) return;
        if (
          fellowsResponse?.status === 200 &&
          fellowsResponse?.data?.data?.length > 0
        ) {
          const fellow = fellowsResponse?.data?.data?.[0];
          const fellowLabel = fellow?.attributes?.label;
          const isDeceased = fellow?.attributes?.active === false;
          const isDoNotContact = fellow?.attributes?.doNotContact === true;

          let message;
          if (isDeceased) {
            message = `This email belongs to ${fellowLabel || 'a fellow'}. Please note this fellow is deceased and cannot be selected.`;
          } else if (isDoNotContact) {
            message = `This email belongs to ${fellowLabel || 'a fellow'}. Please note this fellow has requested not to be contacted.`;
          } else {
            message = `Please select existing AAS referee${fellowLabel ? `: ${fellowLabel}` : ''}`;
          }

          setError(name, { type: 'required', message });
        }

        const nonAasResponse = await axiosGet(
          `non-aas-referees?filters[email][$eq]=${watchEmail}`,
        );
        if (cancelled) return;
        if (
          nonAasResponse?.status === 200 &&
          nonAasResponse?.data?.data?.length > 0
        ) {
          const referee = nonAasResponse?.data?.data?.[0];
          const refereeId = referee?.id;
          const refereeLabel = referee?.attributes?.label;
          const isInactive = referee?.attributes?.isActive === false;

          const updatedAt = referee?.attributes?.updatedAt;
          const updatedAtTime = dayjs(updatedAt);
          const currentTime = dayjs();
          const differenceInSeconds = currentTime.diff(updatedAtTime, 'second');

          if (differenceInSeconds > 50) {
            let isBlocked = false;
            if (refereeId) {
              const userResponse = await axiosGet(
                `users?filters[nonAasRefereeId][$eq]=${refereeId}`,
              );
              if (cancelled) return;
              if (
                userResponse?.status === 200 &&
                userResponse?.data?.length > 0
              ) {
                isBlocked = userResponse?.data?.[0]?.blocked === true;
              }
            }

            let message;
            if (isBlocked) {
              message = `${refereeLabel || 'This referee'} is pending approval. Please contact fellowship@science.org.au for further information.`;
            } else if (isInactive) {
              message = `This email belongs to ${refereeLabel || 'a non-AAS referee'}. Please note this referee is inactive and cannot be selected.`;
            } else {
              message = `Please select existing non AAS referee${refereeLabel ? `: ${refereeLabel}` : ''}`;
            }

            setError(name, { type: 'required', message });
          }
        }
      } catch (err) {
        devError('InputEmail referee lookup failed:', err);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [name, setError, watchEmail]);

  const isUserChange = useRef(false);

  useEffect(() => {
    isUserChange.current = false;
  }, [name]);

  useEffect(() => {
    if (!/email/i.test(name)) return undefined;
    if (!isUserChange.current) return undefined;

    const educationalInstitutionKey = `${name}EducationalInstitution`;

    if (!watchEmail || !isValidEmail(watchEmail) || watchEmail.indexOf('@') === -1 || watchEmail.indexOf('.') === -1) {
      setValue(educationalInstitutionKey, '');
      if (name === 'email') {
        setValue('organisationId', null, { shouldValidate: true });
        setValue('otherOrganisation', '', { shouldValidate: true });
      }
      return undefined;
    }

    const domain = watchEmail.split('@')[1];
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await axiosGet(
          `educational-institution?filters[email][$eq]=${domain}`,
        );
        if (cancelled) return;

        const messages = result?.data?.message;
        if (
          Array.isArray(messages) &&
          messages[0] !== undefined &&
          messages[1] === undefined
        ) {
          const [educationalInstitution] = messages;
          setValue(educationalInstitutionKey, educationalInstitution);
          if (name === 'email' && educationalInstitution) {
            setValue('otherOrganisation', educationalInstitution, {
              shouldValidate: true,
            });
          }
        } else {
          setValue(educationalInstitutionKey, '');
          if (name === 'email') {
            setValue('organisationId', null, { shouldValidate: true });
            setValue('otherOrganisation', '', { shouldValidate: true });
          }
        }
      } catch (err) {
        if (cancelled) return;
        devError('InputEmail educational institution lookup failed:', err);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [name, watchEmail, setValue]);

  return (
    <>
      <input
        name={name}
        className='form-control mb-3'
        type='email'
        aria-invalid={!!errors?.[name]}
        {...register(name, {
          onChange: () => {
            isUserChange.current = true;
          },
        })}
      />

      {watch(`${name}EducationalInstitution`) !== undefined &&
      watch(`${name}EducationalInstitution`) !== '' &&
      !errors[name] ? (
        <input
          className='form-control mt-3'
          type='text'
          name={`${name}EducationalInstitution`}
          {...register(`${name}EducationalInstitution`)}
          disabled
        />
      ) : (
        ''
      )}
    </>
  );
};

InputEmail.propTypes = {
  name: PropTypes.string,
  errors: PropTypes.object,
};

export default InputEmail;
